/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Input } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { useLanguage } from 'hooks/Language/useLanguage';
import { CSSProperties, useState } from 'react';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import './index.scss';

const { Search } = Input;

interface FormItemInputProps {
  name?: string;
  label?: string;
  rules?: any;
  className?: string;
  placeholder?: string;
  size?: SizeType;
  addonBefore?: React.ReactNode;
  prefix?: React.ReactNode | string | number;
  suffix?: React.ReactNode | string | number;
  hidden?: boolean;
  type?: string | any;
  password?: boolean;
  loading?: boolean;
  showLoading?: boolean;
  addonAfter?: React.ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
  onChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  formItemStyle?: CSSProperties;
  minLength?: number;
  maxLength?: number;
  autoComplete?: string;
  allowClear?: boolean;
  status?: '' | 'error' | 'warning';
  tooltip?: React.ReactNode;
  initialValue?: any;
}

const FormItemInput: React.FC<FormItemInputProps> = ({
  name,
  label,
  rules,
  className = '',
  placeholder,
  size = 'large',
  addonBefore,
  prefix,
  suffix,
  hidden = false,
  type = 'text',
  password = false,
  onChange,
  onFocus,
  onBlur,
  loading,
  showLoading = false,
  addonAfter,
  disabled = false,
  style,
  formItemStyle,
  minLength,
  maxLength,
  autoComplete = 'off',
  allowClear = false,
  status,
  tooltip,
  initialValue,
}) => {
  const { translate } = useLanguage();
  const [isFocused, setIsFocused] = useState(false);

  // Custom handlers to track focus state
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus && onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur && onBlur(e);
  };

  // Generate input class names based on state
  const getInputClassName = () => {
    let classes = 'modern-input';
    if (isFocused) classes += ' input-focused';
    if (disabled) classes += ' input-disabled';
    return classes;
  };

  // Input common props to reduce code duplication
  const commonInputProps = {
    type,
    hidden,
    placeholder: placeholder ? translate(placeholder) : undefined,
    size,
    addonBefore,
    addonAfter,
    prefix,
    suffix,
    disabled,
    style: {
      borderRadius: '8px',
      padding: size === 'large' ? '12px 16px' : undefined,
      transition: 'all 0.2s ease',
      ...style,
    },
    maxLength,
    minLength,
    onChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    autoComplete,
    allowClear,
    className: getInputClassName(),
  };

  return (
    <Form.Item
      name={name}
      label={label ? translate(label) : undefined}
      rules={rules}
      className={`modern-form-item ${className}`}
      style={formItemStyle}
      tooltip={tooltip}
      status={status}
      initialValue={initialValue}
    >
      {password ? (
        <Input.Password
          {...commonInputProps}
          iconRender={(visible) =>
            visible ? (
              <EyeOutlined className="password-icon" />
            ) : (
              <EyeInvisibleOutlined className="password-icon" />
            )
          }
        />
      ) : showLoading ? (
        <Search {...commonInputProps} loading={loading} />
      ) : (
        <Input {...commonInputProps} />
      )}
    </Form.Item>
  );
};

export default FormItemInput;
